/**
 * Feedback card configuration
 * Questions, timing, and helper functions for the user feedback feature
 */

export type QuestionType = 'rating' | 'yes_no' | 'freeform';

export interface FeedbackQuestion {
  id: number;
  text: string;
  type: QuestionType;
  options?: string[]; // For rating: ['1', '2', '3', '4', '5'], for yes_no: ['Yes', 'No', 'Neutral']
  lowLabel?: string; // Label for lowest rating (e.g., "Poor", "Very difficult")
  highLabel?: string; // Label for highest rating (e.g., "Excellent", "Very easy")
}

/**
 * Survey questions (19 questions, excluding the free-form which appears every 5th position)
 * Categories: Usability, Feature Satisfaction, Value & Performance, Recommendation & Loyalty, Open-Ended
 */
export const FEEDBACK_QUESTIONS: FeedbackQuestion[] = [
  // Usability (Rating 1-5)
  {
    id: 1,
    text: 'How easy is Flouri to use overall?',
    type: 'rating',
    options: ['1', '2', '3', '4', '5'],
    lowLabel: 'Very difficult',
    highLabel: 'Very easy',
  },
  {
    id: 2,
    text: 'How intuitive is it to navigate the dashboard?',
    type: 'rating',
    options: ['1', '2', '3', '4', '5'],
    lowLabel: 'Not intuitive',
    highLabel: 'Very intuitive',
  },
  {
    id: 3,
    text: 'How easy is it to find what you\'re looking for?',
    type: 'rating',
    options: ['1', '2', '3', '4', '5'],
    lowLabel: 'Very difficult',
    highLabel: 'Very easy',
  },
  {
    id: 4,
    text: 'How would you rate the campaign setup process?',
    type: 'rating',
    options: ['1', '2', '3', '4', '5'],
    lowLabel: 'Poor',
    highLabel: 'Excellent',
  },
  // Feature Satisfaction (Yes/No/Neutral)
  {
    id: 5,
    text: 'Is the budget optimization helping your campaigns?',
    type: 'yes_no',
    options: ['Yes', 'No', 'Neutral'],
  },
  {
    id: 6,
    text: 'Are the AI recommendations useful for your goals?',
    type: 'yes_no',
    options: ['Yes', 'No', 'Neutral'],
  },
  {
    id: 7,
    text: 'Is the reporting meeting your needs?',
    type: 'yes_no',
    options: ['Yes', 'No', 'Neutral'],
  },
  {
    id: 8,
    text: 'Does the Google Ads linking process work smoothly?',
    type: 'yes_no',
    options: ['Yes', 'No', 'Neutral'],
  },
  // Value & Performance (Rating 1-5)
  // {
  //   id: 9,
  //   text: 'How valuable is Flouri for your advertising efforts?',
  //   type: 'rating',
  //   options: ['1', '2', '3', '4', '5'],
  //   lowLabel: 'Not valuable',
  //   highLabel: 'Very valuable',
  // },
  // {
  //   id: 10,
  //   text: 'How would you rate the quality of optimization suggestions?',
  //   type: 'rating',
  //   options: ['1', '2', '3', '4', '5'],
  //   lowLabel: 'Poor',
  //   highLabel: 'Excellent',
  // },
  // {
  //   id: 11,
  //   text: 'How satisfied are you with campaign performance insights?',
  //   type: 'rating',
  //   options: ['1', '2', '3', '4', '5'],
  //   lowLabel: 'Not satisfied',
  //   highLabel: 'Very satisfied',
  // },
  {
    id: 12,
    text: 'How well does Flouri save you time on ad management?',
    type: 'rating',
    options: ['1', '2', '3', '4', '5'],
    lowLabel: 'Not well',
    highLabel: 'Very well',
  },
  // Recommendation & Loyalty (Yes/No/Neutral)
  {
    id: 13,
    text: 'Would you recommend Flouri to a colleague?',
    type: 'yes_no',
    options: ['Yes', 'No', 'Neutral'],
  },
  // {
  //   id: 14,
  //   text: 'Has Flouri improved your Google Ads results?',
  //   type: 'yes_no',
  //   options: ['Yes', 'No', 'Neutral'],
  // },
  {
    id: 15,
    text: 'Do you feel confident using Flouri\'s optimization features?',
    type: 'yes_no',
    options: ['Yes', 'No', 'Neutral'],
  },
  // Open-Ended Feedback
  {
    id: 16,
    text: 'What feature would you most like to see added?',
    type: 'freeform',
  },
  {
    id: 17,
    text: 'What\'s your biggest challenge with Google Ads?',
    type: 'freeform',
  },
  {
    id: 18,
    text: 'What would make Flouri more valuable for you?',
    type: 'freeform',
  },
  {
    id: 19,
    text: 'What\'s one thing we could improve?',
    type: 'freeform',
  },
];

/**
 * Free-form question that appears at every 5th position (5, 10, 15, 20...)
 */
export const FREEFORM_QUESTION: FeedbackQuestion = {
  id: 20,
  text: 'Is there anything about your experience with Flouri you\'d like to share with us?',
  type: 'freeform',
};

/**
 * Default timing configuration for feedback display
 * These are fallback values - actual values come from config.json via API
 */
export const FEEDBACK_CONFIG_DEFAULTS = {
  initialDelayMinutes: 10,
  afterAnswerDelayMinutes: 20,
  autoAdvanceDelayMinutes: 5,
};

/**
 * Static configuration for feedback display
 */
export const FEEDBACK_CONFIG = {
  // Interval at which free-form question appears
  FREEFORM_INTERVAL: 5,

  // Session storage keys
  STORAGE_KEYS: {
    SHUFFLED_SEQUENCE: 'feedback_shuffled_sequence',
    CURRENT_POSITION: 'feedback_current_position',
    SESSION_START: 'feedback_session_start',
    LAST_SHOWN_AT: 'feedback_last_shown_at',
    LAST_ANSWERED_AT: 'feedback_last_answered_at',
    CONFIG_CACHE: 'feedback_config_cache',
    IS_MINIMIZED: 'feedback_is_minimized',
  },
} as const;

/**
 * Feedback timing configuration (loaded from API)
 */
export interface FeedbackTimingConfig {
  initialDelayMinutes: number;
  afterAnswerDelayMinutes: number;
  autoAdvanceDelayMinutes: number;
}

/**
 * Get timing values in milliseconds
 */
export function getTimingMs(config: FeedbackTimingConfig) {
  return {
    INITIAL_DELAY_MS: config.initialDelayMinutes * 60 * 1000,
    AFTER_ANSWER_DELAY_MS: config.afterAnswerDelayMinutes * 60 * 1000,
    AUTO_ADVANCE_DELAY_MS: config.autoAdvanceDelayMinutes * 60 * 1000,
  };
}

/**
 * Shuffle an array using Fisher-Yates algorithm
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Generate a shuffled sequence of question IDs for this session
 * Excludes the free-form question (id: 20) as it has fixed positions
 */
export function generateShuffledSequence(): number[] {
  const questionIds = FEEDBACK_QUESTIONS.map((q) => q.id);
  return shuffleArray(questionIds);
}

/**
 * Get the question to show at a given position in the session
 * @param position - 1-indexed position in the session (1, 2, 3, ...)
 * @param shuffledSequence - Pre-shuffled array of question IDs
 * @returns The question to show, or null if sequence is exhausted
 */
export function getQuestionAtPosition(
  position: number,
  shuffledSequence: number[]
): FeedbackQuestion | null {
  // Every 5th position shows the free-form question
  if (position > 0 && position % FEEDBACK_CONFIG.FREEFORM_INTERVAL === 0) {
    return FREEFORM_QUESTION;
  }

  // Calculate the index in the shuffled sequence
  // Account for free-form questions taking up every 5th slot
  const freeformCount = Math.floor((position - 1) / FEEDBACK_CONFIG.FREEFORM_INTERVAL);
  const sequenceIndex = position - 1 - freeformCount;

  if (sequenceIndex >= shuffledSequence.length) {
    return null; // Sequence exhausted
  }

  const questionId = shuffledSequence[sequenceIndex];
  return FEEDBACK_QUESTIONS.find((q) => q.id === questionId) || null;
}

/**
 * Get or create the shuffled sequence for this session
 */
export function getOrCreateShuffledSequence(): number[] {
  const stored = sessionStorage.getItem(FEEDBACK_CONFIG.STORAGE_KEYS.SHUFFLED_SEQUENCE);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      // Invalid stored value, regenerate
    }
  }

  const sequence = generateShuffledSequence();
  sessionStorage.setItem(
    FEEDBACK_CONFIG.STORAGE_KEYS.SHUFFLED_SEQUENCE,
    JSON.stringify(sequence)
  );
  return sequence;
}
